import { Book } from '@/utils/bookRouting';
import { clientIdentityHeaders } from '@/utils/buildInfo';
import { parseUpgradeRequired, tripClientGate } from './clientGate';
import {
  readCachedCatalog,
  readCachedReaderBook,
  removeCachedReaderBook,
  writeCachedCatalog,
  writeCachedReaderBook,
} from './readerCacheStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export interface Collection {
  name: string;
  description: string;
  books: Book[];
}

export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

export interface LibraryFacetsResponse {
  centuries: FacetOption[];
  durations: FacetOption[];
}

export interface LibraryBooksResponse {
  items: Book[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  sort: string;
}

interface LibraryBooksApiResponse {
  items: LibraryBookCardDto[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  sort: string;
}

interface ReaderWordSyncDto {
  available: boolean;
  token_count: number;
  timing_count: number;
}

interface ReaderChapterVoiceDto {
  audio_url: string;
  timings_url: string;
  duration_ms: number;
  word_sync: ReaderWordSyncDto;
  // Declared sizes are optional in the contract: absent for any bundle not
  // re-ingested since they were added, and never to be read as zero.
  audio_bytes?: number | null;
  timings_bytes?: number | null;
}

export interface ReaderChapterDto {
  id: string;
  index: number;
  parent_id: string | null;
  title: string;
  basename: string;
  content_mode: 'standard' | 'text_only';
  html_url: string;
  token_map_url: string | null;
  html_bytes?: number | null;
  token_map_bytes?: number | null;
  token_count: number;
  word_count: number;
  character_count: number;
  block_count: number;
  heading_count: number;
  paragraph_count: number;
  image_count: number;
  voices: Record<string, ReaderChapterVoiceDto>;
}

export interface ReaderBookDto {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  author: string;
  description: string | null;
  publication_year: string | null;
  render_profile: string;
  total_duration_ms: number;
  word_count: number;
  featured: boolean;
  categories: string[];
  expert_reviews: Array<{
    editor_name: string;
    editor_title: string;
    review_quote: string;
  }>;
  default_voice_key: string;
  reading_voices: Array<{
    voice_key: string;
    display_name: string;
    is_default: boolean;
  }>;
  ebook: {
    format: string;
    css_urls: string[];
  };
  cover: {
    image_url: string;
    animation_url: string | null;
  };
  chapters: ReaderChapterDto[];
}

interface LibraryBookCardDto {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  author: string;
  description: string | null;
  publication_year: string | null;
  render_profile: string;
  total_duration_ms: number;
  word_count: number;
  featured: boolean;
  cover_url: string;
  cover_animation_url: string | null;
  categories: string[];
  expert_reviews: Array<{
    editor_name: string;
    editor_title: string;
    review_quote: string;
  }>;
}

interface CollectionDto {
  name: string;
  description: string;
  books: LibraryBookCardDto[];
}

export interface ReaderWordSync {
  available: boolean;
  tokenCount: number;
  timingCount: number;
}

export interface ReaderReadingVoice {
  voiceKey: string;
  displayName: string;
  isDefault: boolean;
}

export interface ReaderChapterVoice {
  audioUrl: string;
  timingsUrl: string;
  durationMs: number;
  wordSync: ReaderWordSync;
  /**
   * Declared size in bytes; null when the bundle did not say. Optional
   * because payloads persisted by the reader cache before these fields
   * existed have neither, and a stale copy must still type as a book.
   */
  audioBytes?: number | null;
  timingsBytes?: number | null;
}

export interface ReaderChapter {
  id: string;
  index: number;
  parentId: string | null;
  title: string;
  basename: string;
  contentMode: 'standard' | 'text_only';
  htmlUrl: string;
  tokenMapUrl: string | null;
  /** Declared size in bytes; null when the bundle did not say. Optional for the same reason as on the voice. */
  htmlBytes?: number | null;
  tokenMapBytes?: number | null;
  tokenCount: number;
  wordCount: number;
  characterCount: number;
  blockCount: number;
  headingCount: number;
  paragraphCount: number;
  imageCount: number;
  voices: Record<string, ReaderChapterVoice>;
}

export interface ReaderBook {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  author: string;
  description: string | null;
  publicationYear: string | null;
  renderProfile: 'chapter-fragments-v1';
  totalDurationMs: number;
  wordCount: number;
  featured: boolean;
  categories: string[];
  expertReviews: Array<{
    editorName: string;
    editorTitle: string;
    reviewQuote: string;
  }>;
  defaultVoiceKey: string;
  readingVoices: ReaderReadingVoice[];
  ebook: {
    format: 'chapter-fragments-v1';
    cssUrls: string[];
  };
  cover: {
    imageUrl: string;
    animationUrl: string | null;
  };
  chapters: ReaderChapter[];
}

const readerBookCache = new Map<string, ReaderBook>();
const readerBookRequests = new Map<string, Promise<ReaderBook>>();

function mapWordSync(dto: ReaderWordSyncDto): ReaderWordSync {
  return {
    available: dto.available,
    tokenCount: dto.token_count,
    timingCount: dto.timing_count,
  };
}

/**
 * A declared asset size, or null. Sizes are hints from ingest, not guarantees:
 * a bundle ingested before they were recorded has none, and anything that is
 * not a plain non-negative number is treated as absent rather than as zero,
 * which a size preflight would read as "this asset is empty".
 */
const declaredBytes = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;

function mapReaderChapter(dto: ReaderChapterDto): ReaderChapter {
  const voices = Object.fromEntries(
    Object.entries(dto.voices).map(([voiceKey, voice]) => [
      voiceKey,
      {
        audioUrl: voice.audio_url,
        timingsUrl: voice.timings_url,
        durationMs: voice.duration_ms,
        wordSync: mapWordSync(voice.word_sync),
        audioBytes: declaredBytes(voice.audio_bytes),
        timingsBytes: declaredBytes(voice.timings_bytes),
      },
    ])
  );

  return {
    id: dto.id,
    index: dto.index,
    parentId: dto.parent_id,
    title: dto.title,
    basename: dto.basename,
    contentMode: dto.content_mode,
    htmlUrl: dto.html_url,
    tokenMapUrl: dto.token_map_url,
    htmlBytes: declaredBytes(dto.html_bytes),
    tokenMapBytes: declaredBytes(dto.token_map_bytes),
    tokenCount: dto.token_count,
    wordCount: dto.word_count,
    characterCount: dto.character_count,
    blockCount: dto.block_count,
    headingCount: dto.heading_count,
    paragraphCount: dto.paragraph_count,
    imageCount: dto.image_count,
    voices,
  };
}

export function mapReaderBookDto(dto: ReaderBookDto): ReaderBook {
  if (dto.render_profile !== 'chapter-fragments-v1' || dto.ebook.format !== 'chapter-fragments-v1') {
    throw new Error(`Unsupported reader format '${dto.render_profile}/${dto.ebook.format}'`);
  }

  const defaultVoices = dto.reading_voices.filter((voice) => voice.is_default);
  if (defaultVoices.length !== 1 || defaultVoices[0].voice_key !== dto.default_voice_key) {
    throw new Error('Reader response must declare exactly one matching default reading voice');
  }

  const readingVoiceKeys = new Set(dto.reading_voices.map((voice) => voice.voice_key));
  if (readingVoiceKeys.size !== dto.reading_voices.length) {
    throw new Error('Reader response contains duplicate reading voice keys');
  }

  const chapters = dto.chapters.map(mapReaderChapter);
  for (const chapter of chapters) {
    if (chapter.contentMode === 'standard') {
      if (!chapter.tokenMapUrl || !chapter.voices[dto.default_voice_key]) {
        throw new Error(`Standard chapter '${chapter.id}' is missing its token map or default voice`);
      }
      const chapterVoiceKeys = Object.keys(chapter.voices);
      if (
        chapterVoiceKeys.length !== readingVoiceKeys.size ||
        chapterVoiceKeys.some((voiceKey) => !readingVoiceKeys.has(voiceKey))
      ) {
        throw new Error(`Standard chapter '${chapter.id}' does not contain every declared reading voice`);
      }
    } else if (chapter.contentMode === 'text_only') {
      if (chapter.tokenMapUrl !== null || Object.keys(chapter.voices).length !== 0) {
        throw new Error(`Text-only chapter '${chapter.id}' contains runtime audio or token assets`);
      }
    }
  }

  return {
    id: dto.id,
    slug: dto.slug,
    title: dto.title,
    subtitle: dto.subtitle,
    author: dto.author,
    description: dto.description,
    publicationYear: dto.publication_year,
    renderProfile: 'chapter-fragments-v1',
    totalDurationMs: dto.total_duration_ms,
    wordCount: dto.word_count,
    featured: dto.featured,
    categories: dto.categories,
    expertReviews: dto.expert_reviews.map((review) => ({
      editorName: review.editor_name,
      editorTitle: review.editor_title,
      reviewQuote: review.review_quote,
    })),
    defaultVoiceKey: dto.default_voice_key,
    readingVoices: dto.reading_voices.map((voice) => ({
      voiceKey: voice.voice_key,
      displayName: voice.display_name,
      isDefault: voice.is_default,
    })),
    ebook: {
      format: 'chapter-fragments-v1',
      cssUrls: dto.ebook.css_urls,
    },
    cover: {
      imageUrl: dto.cover.image_url,
      animationUrl: dto.cover.animation_url,
    },
    chapters,
  };
}

export const getReaderChapterVoice = (
  chapter: ReaderChapter | null | undefined,
  voiceKey: string | null | undefined,
): ReaderChapterVoice | null => {
  if (!chapter || chapter.contentMode !== 'standard' || !voiceKey) {
    return null;
  }
  return chapter.voices[voiceKey] ?? null;
};

function parsePublicationYearValue(publicationYear: string | null): number | null | undefined {
  if (!publicationYear) {
    return null;
  }
  const parsed = Number.parseInt(publicationYear, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function mapCatalogBookDtoToBook(dto: LibraryBookCardDto): Book {
  return {
    id: dto.id,
    slug: dto.slug || dto.id,
    title: dto.title,
    subheading: dto.subtitle ?? undefined,
    author: dto.author,
    featured: dto.featured,
    totalDuration: (dto.total_duration_ms || 0) / 1000,
    releaseYear: parsePublicationYearValue(dto.publication_year),
    description: dto.description ?? undefined,
    publicationYear: dto.publication_year,
    wordCount: dto.word_count,
    renderProfile: dto.render_profile,
    categories: dto.categories,
    expertReviews: dto.expert_reviews.map((review) => ({
      editorName: review.editor_name,
      editorTitle: review.editor_title,
      reviewQuote: review.review_quote,
    })),
    coverUrl: dto.cover_url,
    coverAnimationUrl: dto.cover_animation_url,
  };
}

/**
 * The catalog, falling back to the last one persisted when the request fails.
 *
 * Without this a failed boot yields an empty shelf, which reads as "your books
 * are gone" rather than "we could not reach the server".
 */
export async function fetchBooks(): Promise<Book[]> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/library/books?page=1&page_size=500`);
  } catch (error) {
    const persisted = readCachedCatalog();
    if (persisted) {
      console.warn('[catalog] Offline; serving the on-device catalog.');
      return persisted;
    }
    throw error;
  }

  if (!response.ok) {
    const persisted = readCachedCatalog();
    if (persisted) {
      console.warn(`[catalog] Unavailable (${response.status}); serving the on-device catalog.`);
      return persisted;
    }
    throw new Error(`Failed to fetch books: ${response.statusText}`);
  }

  const data = (await response.json()) as LibraryBooksApiResponse;
  const books = data.items.map(mapCatalogBookDtoToBook);
  writeCachedCatalog(books);
  return books;
}

export async function fetchCollections(): Promise<Collection[]> {
  const response = await fetch(`${API_BASE_URL}/api/collections`);

  if (!response.ok) {
    throw new Error(`Failed to fetch collections: ${response.statusText}`);
  }

  const data = (await response.json()) as CollectionDto[];
  return data.map((collection) => ({
    name: collection.name,
    description: collection.description,
    books: collection.books.map(mapCatalogBookDtoToBook),
  }));
}

export async function fetchLibraryFacets(): Promise<LibraryFacetsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/library/facets`);

  if (!response.ok) {
    throw new Error(`Failed to fetch library facets: ${response.statusText}`);
  }

  return response.json();
}

interface FetchLibraryBooksParams {
  q?: string;
  centuries?: string[];
  duration?: string[];
  sort?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchLibraryBooks(
  params: FetchLibraryBooksParams = {},
  signal?: AbortSignal,
): Promise<LibraryBooksResponse> {
  const searchParams = new URLSearchParams();

  if (params.q) {
    searchParams.set('q', params.q);
  }

  params.centuries?.forEach((value) => searchParams.append('centuries', value));
  params.duration?.forEach((value) => searchParams.append('duration', value));

  if (params.sort) {
    searchParams.set('sort', params.sort);
  }

  if (params.page) {
    searchParams.set('page', String(params.page));
  }

  if (params.pageSize) {
    searchParams.set('page_size', String(params.pageSize));
  }

  const query = searchParams.toString();
  const response = await fetch(`${API_BASE_URL}/api/library/books${query ? `?${query}` : ''}`, { signal });

  if (!response.ok) {
    throw new Error(`Failed to fetch library books: ${response.statusText}`);
  }

  const data = (await response.json()) as LibraryBooksApiResponse;
  return {
    items: data.items.map(mapCatalogBookDtoToBook),
    page: data.page,
    pageSize: data.pageSize,
    total: data.total,
    totalPages: data.totalPages,
    sort: data.sort,
  };
}

/**
 * The reader payload for a book, preferring the network whenever it can be
 * reached so an online reader never drifts onto a stale copy.
 *
 * The persisted copy is a fallback, not a shortcut: it answers only once a
 * request has actually failed. That keeps freshness exactly as it was online
 * while letting an offline cold start open a book the user has read before.
 */
export interface FetchReaderBookOptions {
  /**
   * Whether a 426 from the server may put up the upgrade screen. True for
   * anything the user is waiting on; false for background work.
   *
   * The distinction exists because the screen replaces the whole React tree,
   * the audio element included. A downloaded book plays fine on a build the
   * server has stopped serving, so letting a background payload fetch block
   * the app would stop playback mid-chapter for someone whose app was, from
   * where they sat, working — the same failure the offline work spent a device
   * pass removing. A refusal the user did not ask for is recorded by the
   * server's log line and otherwise waits until they open a book.
   */
  blockOnUpgradeRequired?: boolean;
}

export async function fetchReaderBook(
  bookId: string,
  signal?: AbortSignal,
  options: FetchReaderBookOptions = {},
): Promise<ReaderBook> {
  const { blockOnUpgradeRequired = true } = options;
  const cachedBook = readerBookCache.get(bookId);
  if (cachedBook) {
    return cachedBook;
  }

  const pendingRequest = signal ? undefined : readerBookRequests.get(bookId);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = (async () => {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/api/library/books/${bookId}`, {
        signal,
        // This is the call the version gate watches: it carries the reader
        // contract, so it is where a too-old build has to be turned away.
        headers: clientIdentityHeaders(),
      });
    } catch (error) {
      // Never reached a server. An abort is a caller decision, not an outage,
      // so it propagates rather than resolving to a cached book.
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      const persisted = adoptPersistedReaderBook(bookId);
      if (persisted) {
        console.warn(`[reader] Offline; serving book ${bookId} from the on-device cache.`);
        return persisted;
      }
      throw error;
    }

    // Upgrade Required, and it must be read before the cache fallback below.
    // A 426 is not an outage — it is the server saying this build can no
    // longer understand the payload — so answering it from the on-device copy
    // would hand the reader a book it cannot render and swallow the gate
    // entirely. That fallback reaches further than it used to: since the
    // offline store landed, a pinned or recently-read book is on disk for
    // exactly the users a breaking change strands.
    if (
      response.status === 426
      && blockOnUpgradeRequired
      && tripClientGate(await parseUpgradeRequired(response))
    ) {
      throw new Error(`Client too old to load reader book ${bookId}`);
    }

    if (!response.ok) {
      const persisted = adoptPersistedReaderBook(bookId);
      if (persisted) {
        console.warn(
          `[reader] Book ${bookId} unavailable (${response.status}); serving the on-device cache.`,
        );
        return persisted;
      }
      throw new Error(`Failed to fetch reader book ${bookId}: ${response.statusText}`);
    }

    const data = (await response.json()) as ReaderBookDto;
    const readerBook = mapReaderBookDto(data);
    readerBookCache.set(bookId, readerBook);
    writeCachedReaderBook(bookId, readerBook);
    return readerBook;
  })();

  if (!signal) {
    readerBookRequests.set(bookId, request);
  }

  try {
    return await request;
  } finally {
    if (!signal) {
      readerBookRequests.delete(bookId);
    }
  }
}

/** Promotes a persisted payload into the in-memory cache, so later synchronous
 * reads see it too. */
const adoptPersistedReaderBook = (bookId: string): ReaderBook | null => {
  const persisted = readCachedReaderBook(bookId);
  if (persisted) {
    readerBookCache.set(bookId, persisted);
  }
  return persisted;
};

export const getCachedReaderBook = (bookId: string): ReaderBook | null =>
  readerBookCache.get(bookId) ?? adoptPersistedReaderBook(bookId);

/**
 * Forget a book everywhere, disk included: this backs the reader's retry, and
 * leaving the persisted copy behind would make retry hand back the very payload
 * the user is trying to get past.
 */
export const invalidateReaderBook = (bookId: string): void => {
  readerBookCache.delete(bookId);
  readerBookRequests.delete(bookId);
  removeCachedReaderBook(bookId);
};

export const preloadReaderBook = (bookId: string): Promise<ReaderBook> => fetchReaderBook(bookId);
